/*
 *
 * ModifyNumberModal
 *
 * @version 1.0.0
 * @author Pietro Bertuccelli
 * @since 09-Sep-2024
 */

import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import * as Yup from 'yup'

import PortalPhoneInput from '@nexidigital/portalphoneinput'
import MerchantModal from '@nexidigital/merchantmodal'
import MerchantGenericCard from '@nexidigital/merchantgenericcard'
import { AnalyticsContext } from 'Hoc/AnalyticsProvider'

import Toggle from 'Hoc/ToggleComponent'

import formattedMessage from './languageModule'
import './ModifyNumberModal.scss'

const ModifyNumberModal = (props) => {
  const intl = useIntl()
  const [isError, setIsError] = useState(false)
  const analyticsContext = useContext(AnalyticsContext)
  const { setFormInteraction } = analyticsContext || {}

  const PHONE_PREFIX_KEY = 'phonePrefix'
  const PHONE_NUMBER_KEY = 'phoneNumber'

  const phoneNumberValidation = Yup.string().test(
    'invalidPhoneNumberFormat',
    formattedMessage.invalidPhoneNumberFormat,
    function (value) {
      const { phonePrefix } = this.parent

      const italianPrefix = '+39'
      const isItalian = phonePrefix.prefix === italianPrefix
      const regexItalian = /^[0-9]{9,11}$/
      const regexInternational = /^[0-9]{3,15}$/
      if (!value) return true
      return isItalian ? regexItalian.test(value) : regexInternational.test(value)
    }
  )

  const validationSchema = Yup.object().shape({
    [PHONE_NUMBER_KEY]: phoneNumberValidation.required(formattedMessage.requiredError),
    [PHONE_PREFIX_KEY]: Yup.object().shape({
      prefix: Yup.string().required(formattedMessage.requiredError),
      nationId: Yup.string().required(formattedMessage.requiredError)
    })
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      [PHONE_NUMBER_KEY]: props.telephoneNumber || '',
      [PHONE_PREFIX_KEY]: {
        prefix: props.telephonePrefix || (props.defaultPrefix?.prefix ?? ''),
        nationId: props.defaultPrefix?.nationId ?? ''
      }
    },
    validationSchema,
    onSubmit: (values) => {
      props.primaryAction(values)
    },
    validateOnChange: false,
    validateOnBlur: false
  })

  function handleNext () {
    formik.setTouched(
      {
        [PHONE_PREFIX_KEY]: true,
        [PHONE_NUMBER_KEY]: true
      },
      true
    )

    formik.validateForm().then((validationErrors) => {
      if (Object.keys(validationErrors).length === 0) {
        formik.handleSubmit()
      } else {
        formik.setErrors(validationErrors)

        Object.keys(validationErrors).forEach((fieldKey) => {
          const fieldError = validationErrors[fieldKey]
          const errorMessage = fieldError?.props?.defaultMessage || ''

          setFormInteraction(
            'error-field-value',
            'ModifyNumberModal',
            fieldKey,
            formik.errors[fieldKey] ? 0 : 1,
            errorMessage
          )
        })
      }
    })
  }

  // Aggiungiamo un useEffect per impostare il prefisso corretto all'inizializzazione
  useEffect(() => {
    if (props.phonePrefixes && props.phonePrefixes.length > 0 && props.telephonePrefix) {
      const foundPrefix = props.phonePrefixes.find((prefix) => prefix.value === props.telephonePrefix)
      if (foundPrefix) {
        formik.setFieldValue(PHONE_PREFIX_KEY, {
          prefix: foundPrefix.value,
          nationId: foundPrefix.nationId
        })
      }
    }
  }, [props.phonePrefixes, props.telephonePrefix])

  const onPrefixChangeHandler = (selectedPrefix) => {
    formik.setFieldValue(PHONE_PREFIX_KEY, {
      prefix: selectedPrefix?.value ?? '',
      nationId: selectedPrefix?.nationId ?? ''
    })

    // Analytics: Focus event per il prefix selector
    if (setFormInteraction) {
      setFormInteraction(
        'focus',
        'ModifyNumberModal',
        PHONE_NUMBER_KEY,
        formik.errors[PHONE_NUMBER_KEY] ? 0 : 1,
        formik.errors[PHONE_NUMBER_KEY] || ''
      )
    }
  }

  const onPhoneNumberChangeHandler = (event) => {
    const value = event.target.value

    if (/^[0-9\s]*$/.test(value)) {
      setIsError(false)
      formik.setFieldValue(PHONE_NUMBER_KEY, value ?? '')
    }
    setFormInteraction(
      value,
      'ModifyNumberModal',
      PHONE_NUMBER_KEY,
      formik.errors[PHONE_NUMBER_KEY] ? 0 : 1,
      formik.errors[PHONE_NUMBER_KEY] || ''
    )
  }

  // Analytics: Focus-out handler
  const handlePhoneNumberBlur = (event) => {
    if (setFormInteraction) {
      setFormInteraction(
        'focus',
        'ModifyNumberModal',
        PHONE_NUMBER_KEY,
        PHONE_NUMBER_KEY,
        formik.errors[PHONE_NUMBER_KEY] ? 0 : 1,
        formik.errors[PHONE_NUMBER_KEY] || ''
      )
    }
    formik.handleBlur(event)
  }

  useEffect(() => {
    props.actions.doPhonePrefixes()
  }, [])

  // Analytics: Form impression quando il modal viene mostrato
  useEffect(() => {
    if (setFormInteraction) {
      setFormInteraction(
        'impression',
        'ModifyNumberModal',
        PHONE_NUMBER_KEY,
        formik.errors[PHONE_NUMBER_KEY] ? 0 : 1,
        formik.errors[PHONE_NUMBER_KEY] || ''
      )
    }
  }, [])

  return (
    <Toggle>
      <MerchantModal
        title={props.title}
        onClose={props.onClose}
        primaryButtonLabel={props.primaryButtonLabel}
        secondaryButtonLabel={props.secondaryButtonLabel}
        secondaryAction={props.secondaryAction}
        primaryAction={handleNext}
        disableBackdropClick
        isPrimaryDisabled={props.isPhonePrefixesLoading}
        isSecondaryDisabled={props.isPhonePrefixesLoading}
      >
        <MerchantGenericCard>
          <div className='modifyNumberModal'>
            <h1 className='title'>{props.description}</h1>
            <PortalPhoneInput
              type='number'
              isPhonePrefixesLoading={props.isPhonePrefixesLoading}
              phonePrefixes={props.phonePrefixes ?? []}
              selectedPrefix={formik.values[PHONE_PREFIX_KEY].prefix || props.telephonePrefix}
              prefixLabel={formattedMessage.formPrefixLabel}
              analyticsLabel={intl.formatMessage({ id: 't.app.phoneNumber' })}
              placeholder={intl.formatMessage({ id: 't.app.phonePlaceholder' })}
              pathToGetFlags={`${process.env.CMS_URL}/images/flags`}
              pathToDirectFlags={`${process.env.CMS_URL}/images/flags`}
              onPrefixChangeHandler={onPrefixChangeHandler}
              formik={{
                id: PHONE_NUMBER_KEY,
                name: PHONE_NUMBER_KEY,
                error: (formik.touched[PHONE_NUMBER_KEY] && formik.errors[PHONE_NUMBER_KEY]) || isError,
                errorText:
                  formik.touched[PHONE_NUMBER_KEY] && !formik.values[PHONE_NUMBER_KEY]
                    ? formattedMessage.requiredError
                    : formik.errors[PHONE_NUMBER_KEY]
                      ? formik.errors[PHONE_NUMBER_KEY]
                      : isError
                        ? formattedMessage.invalidPhoneNumberFormat
                        : '',
                ...formik.getFieldProps(PHONE_NUMBER_KEY),
                onChange: onPhoneNumberChangeHandler,
                onBlur: handlePhoneNumberBlur
              }}
              labelTooltip={formattedMessage.labelTooltip}
              countryNamePrefixLang={intl.locale}
            />
          </div>
        </MerchantGenericCard>
      </MerchantModal>
    </Toggle>
  )
}
ModifyNumberModal.propTypes = {
  actions: PropTypes.object,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  primaryButtonLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  secondaryButtonLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  secondaryAction: PropTypes.func,
  primaryAction: PropTypes.func,
  onClose: PropTypes.func,
  telephoneNumber: PropTypes.string,
  telephonePrefix: PropTypes.string,
  phonePrefixes: PropTypes.array,
  defaultPrefix: PropTypes.object,
  isPhonePrefixesLoading: PropTypes.bool
}

ModifyNumberModal.defaultProps = {
  actions: {},
  title: '',
  description: '',
  primaryButtonLabel: '',
  secondaryButtonLabel: '',
  secondaryAction: () => {},
  primaryAction: () => {},
  onClose: undefined,
  telephoneNumber: '',
  telephonePrefix: '',
  phonePrefixes: [],
  defaultPrefix: {},
  isPhonePrefixesLoading: false
}

export default ModifyNumberModal
